import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { TastoCercaBarra } from "./CercaRapida";

/** Il nome di ogni pagina nella barra in alto del telefono. Prefisso piu'
 *  lungo vince: `/fatturazione/corrispettivi` prima di `/fatturazione`. */
const TITOLI: Array<[string, string]> = [
  ["/fatturazione/corrispettivi", "Corrispettivi"],
  ["/fatturazione/fatture-estere", "Fatture estere"],
  ["/fatturazione/fatture-fornitori", "Fatture fornitori"],
  ["/fatturazione/controlli", "Controlli fatture"],
  ["/fatturazione/saldi", "Saldi"],
  ["/fatturazione", "Fatture"],
  ["/ordini", "Ordini"],
  ["/messaggi", "Messaggi"],
  ["/clienti", "Clienti"],
  ["/resi", "Resi"],
  ["/reclami", "Reclami corriere"],
  ["/ritiri", "Ritiri"],
  ["/mail", "Mail fornitori"],
  ["/fornitori", "Fornitori"],
  ["/dashboard", "Business Intelligence"],
  ["/impostazioni", "Impostazioni"],
  ["/istruzioni", "Istruzioni"],
];

export function titoloPagina(pathname: string): string {
  const voce = TITOLI.find(([path]) => pathname === path || pathname.startsWith(path + "/"));
  return voce ? voce[1] : "Order Manager";
}

/** Le pagine di DETTAGLIO hanno una testata loro, con la freccia indietro:
 *  la barra sopra sarebbe un secondo titolo che ruba 52px allo schermo. */
export function haTestataPropria(pathname: string): boolean {
  return /^\/(ordini|clienti)\/[^/]+/.test(pathname);
}

const SLOT_ID = "om-topbar-azioni";

/**
 * Barra in alto del telefono (redesign 2026-10-23): nome della pagina a
 * sinistra, tasti della pagina e campanella a destra. Sta FUORI dalla colonna
 * che scorre, quindi resta ferma senza `sticky`.
 *
 * I tasti propri di una pagina (es. «Sincronizza» sugli Ordini) arrivano da
 * `<AzioniBarra>` via portale: la pagina resta padrona della sua logica, la
 * barra decide solo dove metterli.
 */
export function MobileTopBar({ campanella }: { campanella: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <header className="flex h-[52px] shrink-0 items-center gap-2 border-b border-line bg-paper pl-4 pr-1.5">
      <h1 className="min-w-0 flex-1 truncate font-display text-[20px] font-semibold tracking-[-0.015em] text-ink">
        {titoloPagina(pathname)}
      </h1>
      <div id={SLOT_ID} className="flex items-center" />
      <TastoCercaBarra />
      {campanella}
    </header>
  );
}

/** Tasti di pagina da mettere nella barra in alto. Fuori dal telefono (barra
 *  assente) non rende niente: la pagina li mostra gia' nel suo layout. */
export function AzioniBarra({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById(SLOT_ID));
  }, []);
  return slot ? createPortal(children, slot) : null;
}

/** Tasto tondo da 44px della barra: icona sola, nome per lo screen reader. */
export function TastoBarra({
  label,
  onClick,
  children,
  busy = false,
  badge = 0,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  busy?: boolean;
  badge?: number;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={busy}
      className="om-press relative grid h-11 w-11 place-items-center rounded-full text-ink active:bg-paper-2 disabled:opacity-60"
    >
      <span className={busy ? "animate-spin" : undefined}>{children}</span>
      {badge > 0 && (
        <span className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent-2 px-1 text-[10px] font-bold text-white ring-2 ring-paper">
          {badge}
        </span>
      )}
    </button>
  );
}
